import { List as ImmutableList } from 'immutable';

export interface List<T> {
    readonly size: number;
    push(...values: Array<T>): List<T>;
    forEach(
        sideEffect: (value: T, key: number, iter: this) => any,
        context?: any
    ): number;
    concat<C>(...valuesOrCollections: Array<Iterable<C> | C>): List<T | C>;
    filter<F extends T>(
        predicate: (value: T, index: number, iter: List<T>) => value is F,
        context?: any
    ): List<F>;
    filter(
        predicate: (value: T, index: number, iter: List<T>) => any,
        context?: any
    ): List<T>;
    find(
        predicate: (value: T, key: number, iter: List<T>) => boolean,
        context?: any,
        notSetValue?: T
    ): T | undefined;
    every(
        predicate: (value: T, key: number, iter: List<T>) => boolean,
        context?: any
    ): boolean;
    findIndex(
        predicate: (value: T, index: number, iter: List<T>) => boolean,
        context?: any
    ): number;
    map<M>(
        mapper: (value: T, key: number, iter: List<T>) => M,
        context?: any
    ): List<M>;
    splice(index: number, removeNum: number, ...values: Array<T>): List<T>;
    toArray(): Array<T>;
    [Symbol.iterator](): IterableIterator<T>;
    join(separator?: string): string;
    first<NSV>(notSetValue?: NSV): T | NSV;
    delete(index: number): List<T>;
    set(index: number, value: T): List<T>;
    clear(): List<T>;
    some(
        predicate: (value: T, key: number, iter: List<T>) => boolean,
        context?: any
    ): boolean;
    // I changed the key from "V" to "number" and iter become List<T> instead of this
    reduce<R>(
        reducer: (reduction: R, value: T, key: number, iter: List<T>) => R,
        initialReduction: R,
        context?: any
    ): R;
    reduce<R>(
        reducer: (reduction: T | R, value: T, key: number, iter: List<T>) => R
    ): R;
    get<NSV>(index: number, notSetValue: NSV): T | NSV;
    get(index: number): T | undefined;
    unshift(...values: Array<T>): List<T>;
    sort(comparator?: (valueA: T, valueB: T) => number): this;
    count(
        predicate?: (value: T, key: number, iter: List<T>) => boolean,
        context?: any
    ): number;
    slice(begin?: number, end?: number): this;
}

export class ListDecorator<T> implements List<T> {
    private list: ImmutableList<T>;

    constructor(oldList?: Iterable<T>) {
        this.list = ImmutableList(oldList);
    }

    get size() {
        return this.list.size;
    }

    push(...values: Array<T>): this {
        return this.makeMyself(this.list.push(...values));
    }
    filter<F extends T>(
        predicate: (value: T, index: number, iter: List<T>) => value is F,
        context?: any
    ): List<F> {
        return this.list.filter(predicate, context);
    }

    find(
        predicate: (value: T, key: number, iter: List<T>) => boolean,
        context?: any,
        notSetValue?: T
    ): T | undefined {
        return this.list.find(predicate, context, notSetValue);
    }

    findIndex(
        predicate: (value: T, index: number, iter: List<T>) => boolean,
        context?: any
    ): number {
        return this.list.findIndex(predicate, context);
    }

    map<M>(
        mapper: (value: T, key: number, iter: List<T>) => M,
        context?: any
    ): List<M> {
        return this.list.map(mapper, context);
    }

    splice(index: number, removeNum: number, ...values: Array<T>): this {
        return this.makeMyself(
            this.list.splice.apply(this.list, [index, removeNum, ...values])
        );
    }

    toArray() {
        return this.list.toArray();
    }

    first<NSV>(notSetValue?: NSV): T | NSV {
        return this.list.first<NSV>(notSetValue);
    }
    // TOArray

    every(
        predicate: (value: T, key: number, iter: List<T>) => boolean,
        context?: any
    ): boolean {
        return this.list.every(predicate, context);
    }

    // changed type of iter to any to make it work
    forEach(
        sideEffect: (value: T, key: number, iter: any) => any,
        context?: any
    ): number {
        return this.list.forEach(sideEffect, context);
    }

    concat<C>(...valuesOrCollections: Array<Iterable<C> | C>): List<T | C> {
        // Leave these types commented out until it's confirmed it does not
        // break the new Hire Basic app build
        const newInternalList =
            // : List<T | C>
            this.list.concat(
                // <
                //     List<T>,
                //     (C | Iterable<C>)[],
                //     ImmutableList<T | C>
                // >
                ...valuesOrCollections
            );
        return newInternalList as List<T | C>;
    }

    [Symbol.iterator](): IterableIterator<T> {
        return this.list[Symbol.iterator]();
    }

    join(separator?: string): string {
        return this.list.join(separator);
    }
    some(
        predicate: (value: T, key: number, iter: List<T>) => boolean,
        context?: any
    ): boolean {
        return this.list.some(predicate, context);
    }

    delete(index: number): this {
        return this.makeMyself(this.list.delete(index));
    }

    set(index: number, value: T): this {
        return this.makeMyself(this.list.set(index, value));
    }

    clear() {
        return this.makeMyself(this.list.clear());
    }

    reduce<R>(
        reducer: (reduction: R, value: T, key: number, iter: List<T>) => R,
        initialReduction?: R,
        context?: any
    ): R {
        return this.list.reduce(reducer, initialReduction, context);
    }

    get<NSV>(index: number, notSetValue?: NSV): T | NSV {
        return this.list.get<NSV>(index, notSetValue);
    }

    unshift(...values: Array<T>): this {
        return this.makeMyself(this.list.unshift.apply(this, values));
    }

    sort(comparator?: (valueA: T, valueB: T) => number): this {
        return this.makeMyself(this.list.sort(comparator));
    }

    count(
        predicate?: (value: T, key: number, iter: List<T>) => boolean,
        context?: any
    ): number {
        return this.list.count(predicate, context);
    }

    slice(begin?: number, end?: number): this {
        return this.makeMyself(this.list.slice(begin, end));
    }

    // Had to make this super type unspecific so children could override easily
    protected makeMyself(oldList: Iterable<T>): this {
        // The 'new' is requried to actually create a new object.
        // Otherwise the constructor will modify your existing
        // object which violates the immutability
        return new (<any>this.constructor)(oldList);
    }
}
